import { useEffect, useState } from "react";

import { create } from "@bufbuild/protobuf";

import { DownloadSourceArtifactRequestSchema } from "@gx/ts-contracts/services/source/api/source_artifact_pb";

import { useDownloadSourceArtifactMutation } from "@/api/mutations/source-artifacts";

interface UseSourceArtifactContentState {
  content: string | null;
  isLoading: boolean;
  error: Error | null;
  downloadUrl: string | null;
}

const DEFAULT_STATE: UseSourceArtifactContentState = {
  content: null,
  downloadUrl: null,
  isLoading: false,
  error: null,
};

/**
 * Hook for fetching source artifact content from S3.
 * Handles getting presigned URL and fetching content in one place.
 * Note: Uses artifactId (not URI) unlike the project artifact hook.
 */
export const useSourceArtifactContent = (
  artifactId: string | undefined,
): UseSourceArtifactContentState => {
  const { mutateAsync: getDownloadUrl } = useDownloadSourceArtifactMutation();
  const [state, setState] = useState<UseSourceArtifactContentState>(DEFAULT_STATE);

  useEffect(() => {
    if (!artifactId) {
      setState(DEFAULT_STATE);
      return;
    }

    let cancelled = false;

    const fetchContent = async () => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        // Get presigned URL
        const result = await getDownloadUrl(
          create(DownloadSourceArtifactRequestSchema, {
            artifactId,
          }),
        );

        if (cancelled) return;

        if (!result?.downloadUri) {
          throw new Error("No download URL returned");
        }

        setState((prev) => ({ ...prev, downloadUrl: result.downloadUri }));

        // Fetch content from presigned URL
        const response = await fetch(result.downloadUri);
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
        }

        if (cancelled) return;

        const text = await response.text();
        setState((prev) => ({ ...prev, content: text, isLoading: false }));
      } catch (err) {
        if (cancelled) return;
        const fetchError =
          err instanceof Error ? err : new Error("Failed to fetch artifact content");
        setState((prev) => ({ ...prev, error: fetchError, isLoading: false }));
        console.error("Failed to fetch artifact content:", fetchError);
      }
    };

    void fetchContent();

    return () => {
      cancelled = true;
    };
  }, [artifactId, getDownloadUrl]);

  return {
    content: state.content,
    isLoading: state.isLoading,
    error: state.error,
    downloadUrl: state.downloadUrl,
  };
};
